// ABOUTME: Reusable section component for a load spacing field group (UoM + default/min/max/step).
// ABOUTME: Used three times in DeliveryRateForm for timeOnJobSite, spacing, and flowRate sections.

import React, {useMemo} from 'react'
import {Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline} from '@mdi/js'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'
import {useTranslation} from '../../../../hooks/useTranslation'
import {LoadSpacingParameterFormValues} from '../types/loadSpacing'

export interface LoadSpacingFieldGroupSectionProps {
  /** Section heading — passed as a pre-translated string (caller uses t() before passing) */
  title: string
  /** Tooltip for the section title */
  titleTooltip?: string
  /** Current field group values. Null means no overrides — component still renders with zero defaults. */
  value: LoadSpacingParameterFormValues | null
  /** Called whenever any field changes — caller owns the form state */
  onChange: (next: LoadSpacingParameterFormValues) => void
  /** Available UoM options for the dropdown */
  uomOptions: {value: string; label: string; symbol?: string}[]
  /** Whether fields are non-interactive */
  isReadOnly: boolean
  /** Tooltip for the Default field */
  defaultTooltip?: string
  /** Tooltip for the Minimum field */
  minimumTooltip?: string
  /** Tooltip for the Maximum field */
  maximumTooltip?: string
  /** Tooltip for the Step field */
  stepTooltip?: string
  /** Per-field validation error strings */
  errors?: {
    uom?: string
    default?: string
    min?: string
    max?: string
    step?: string
  }
}

export const LoadSpacingFieldGroupSection: React.FC<LoadSpacingFieldGroupSectionProps> = ({
  title,
  titleTooltip,
  value,
  onChange,
  uomOptions,
  isReadOnly,
  defaultTooltip,
  minimumTooltip,
  maximumTooltip,
  stepTooltip,
  errors,
}) => {
  const {t} = useTranslation()

  const safeValue: LoadSpacingParameterFormValues = useMemo(
    () => ({
      uomId: value?.uomId ?? {isOverridden: false, value: null},
      default: value?.default ?? {isOverridden: false, value: null},
      min: value?.min ?? {isOverridden: false, value: null},
      max: value?.max ?? {isOverridden: false, value: null},
      step: value?.step ?? {isOverridden: false, value: null},
    }),
    [value]
  )

  const suffix = useMemo(() => {
    const selectedUomId = value?.uomId?.value ?? ''
    return uomOptions.find((opt) => opt.value === selectedUomId)?.symbol ?? ''
  }, [uomOptions, value?.uomId?.value])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-0.5 items-center">
        <span className="text-lg font-semibold leading-6 text-foreground">{title}</span>
        {titleTooltip && (
          <CountryConfigsTooltip content={titleTooltip} delayMs={300}>
            <span className="text-foreground flex items-center cursor-default">
              <Icon path={mdiInformationOutline} size="regular" />
            </span>
          </CountryConfigsTooltip>
        )}
      </div>

      <div className="flex gap-14 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
          <TextMedium className="text-foreground line-clamp-1">
            {t('countryConfigurations.loadSpacing.uom')}
          </TextMedium>
        </div>
        <div className="w-[228px] shrink-0">
          {errors?.uom ? (
            <CountryConfigsTooltip content={errors.uom} delayMs={0}>
              <div>
                <FilterDropdown
                  value={safeValue.uomId?.value ?? ''}
                  onChange={(v) => onChange({...safeValue, uomId: {isOverridden: true, value: v}})}
                  options={uomOptions}
                  size="regular"
                  disabled={isReadOnly}
                />
              </div>
            </CountryConfigsTooltip>
          ) : (
            <FilterDropdown
              value={safeValue.uomId?.value ?? ''}
              onChange={(v) => onChange({...safeValue, uomId: {isOverridden: true, value: v}})}
              options={uomOptions}
              size="regular"
              disabled={isReadOnly}
            />
          )}
        </div>
      </div>

      <div className="flex gap-14 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
          <div className="flex gap-0.5 items-center whitespace-nowrap">
            <TextMedium className="text-foreground line-clamp-1">
              {t('countryConfigurations.loadSpacing.default')}
            </TextMedium>
            {defaultTooltip && (
              <CountryConfigsTooltip content={defaultTooltip} delayMs={300}>
                <span className="text-foreground flex items-center cursor-default">
                  <Icon path={mdiInformationOutline} size="regular" />
                </span>
              </CountryConfigsTooltip>
            )}
          </div>
        </div>
        <div className="w-[228px] shrink-0">
          {errors?.default ? (
            <CountryConfigsTooltip content={errors.default} delayMs={0}>
              <div>
                <NumericalInput
                  value={safeValue.default?.value ?? 0}
                  size="large"
                  onChange={(v) => onChange({...safeValue, default: {isOverridden: true, value: v}})}
                  suffix={suffix}
                  disabled={isReadOnly}
                  min={0}
                  hasError
                />
              </div>
            </CountryConfigsTooltip>
          ) : (
            <NumericalInput
              value={safeValue.default?.value ?? 0}
              size="large"
              onChange={(v) => onChange({...safeValue, default: {isOverridden: true, value: v}})}
              suffix={suffix}
              disabled={isReadOnly}
              min={0}
            />
          )}
        </div>
      </div>

      <div className="flex gap-14 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
          <div className="flex gap-0.5 items-center whitespace-nowrap">
            <TextMedium className="text-foreground line-clamp-1">
              {t('countryConfigurations.loadSpacing.minimum')}
            </TextMedium>
            {minimumTooltip && (
              <CountryConfigsTooltip content={minimumTooltip} delayMs={300}>
                <span className="text-foreground flex items-center cursor-default">
                  <Icon path={mdiInformationOutline} size="regular" />
                </span>
              </CountryConfigsTooltip>
            )}
          </div>
        </div>
        <div className="w-[228px] shrink-0">
          {errors?.min ? (
            <CountryConfigsTooltip content={errors.min} delayMs={0}>
              <div>
                <NumericalInput
                  value={safeValue.min?.value ?? 0}
                  size="large"
                  onChange={(v) => onChange({...safeValue, min: {isOverridden: true, value: v}})}
                  suffix={suffix}
                  disabled={isReadOnly}
                  min={0}
                  hasError
                />
              </div>
            </CountryConfigsTooltip>
          ) : (
            <NumericalInput
              value={safeValue.min?.value ?? 0}
              size="large"
              onChange={(v) => onChange({...safeValue, min: {isOverridden: true, value: v}})}
              suffix={suffix}
              disabled={isReadOnly}
              min={0}
            />
          )}
        </div>
      </div>

      <div className="flex gap-14 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
          <div className="flex gap-0.5 items-center whitespace-nowrap">
            <TextMedium className="text-foreground line-clamp-1">
              {t('countryConfigurations.loadSpacing.maximum')}
            </TextMedium>
            {maximumTooltip && (
              <CountryConfigsTooltip content={maximumTooltip} delayMs={300}>
                <span className="text-foreground flex items-center cursor-default">
                  <Icon path={mdiInformationOutline} size="regular" />
                </span>
              </CountryConfigsTooltip>
            )}
          </div>
        </div>
        <div className="w-[228px] shrink-0">
          {errors?.max ? (
            <CountryConfigsTooltip content={errors.max} delayMs={0}>
              <div>
                <NumericalInput
                  value={safeValue.max?.value ?? 0}
                  size="large"
                  onChange={(v) => onChange({...safeValue, max: {isOverridden: true, value: v}})}
                  suffix={suffix}
                  disabled={isReadOnly}
                  min={0}
                  hasError
                />
              </div>
            </CountryConfigsTooltip>
          ) : (
            <NumericalInput
              value={safeValue.max?.value ?? 0}
              size="large"
              onChange={(v) => onChange({...safeValue, max: {isOverridden: true, value: v}})}
              suffix={suffix}
              disabled={isReadOnly}
              min={0}
            />
          )}
        </div>
      </div>

      <div className="flex gap-14 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
          <div className="flex gap-0.5 items-center whitespace-nowrap">
            <TextMedium className="text-foreground line-clamp-1">
              {t('countryConfigurations.loadSpacing.step')}
            </TextMedium>
            {stepTooltip && (
              <CountryConfigsTooltip content={stepTooltip} delayMs={300}>
                <span className="text-foreground flex items-center cursor-default">
                  <Icon path={mdiInformationOutline} size="regular" />
                </span>
              </CountryConfigsTooltip>
            )}
          </div>
        </div>
        <div className="w-[228px] shrink-0">
          {errors?.step ? (
            <CountryConfigsTooltip content={errors.step} delayMs={0}>
              <div>
                <NumericalInput
                  value={safeValue.step?.value ?? 0}
                  size="large"
                  onChange={(v) => onChange({...safeValue, step: {isOverridden: true, value: v}})}
                  suffix={suffix}
                  disabled={isReadOnly}
                  min={0}
                  hasError
                />
              </div>
            </CountryConfigsTooltip>
          ) : (
            <NumericalInput
              value={safeValue.step?.value ?? 0}
              size="large"
              onChange={(v) => onChange({...safeValue, step: {isOverridden: true, value: v}})}
              suffix={suffix}
              disabled={isReadOnly}
              min={0}
            />
          )}
        </div>
      </div>
    </div>
  )
}
